import React, { useState } from 'react';
import { cloneDeep } from 'lodash';
import uuid from 'uuid';

import { AlertDialog, Button, Dialog, Icon, RubricTable, Stack, Text, TextArea, TextInput } from 'shell/components';
import { useSnackbar } from 'contexts/snackbar';

const insertAtIndex = (arr, start, ...items) => {
    return [...arr.slice(0, start), ...items, ...arr.slice(start)];
};

const RubricTableWrapper = ({ disabled = false, data, setData, errors }) => {
    const { message } = useSnackbar();
    const [sort, setSort] = useState('desc'); // one of ['desc', 'asc']
    const [dialog, setDialog] = useState({
        createCriterion: { open: false },
        deleteRate: { open: false },
        deleteCriterion: { open: false },
    });
    const [selectedCriterion, setSelectedCriterion] = useState({ criterionId: '', criterionName: '', description: '' });

    // called onBlur of rate inputs and when the sort select value changes
    // sort is one of ['desc', 'asc']
    const handleSort = (sort) => {
        setSort(sort);
        setData((current) => {
            return current.map((criterion) => {
                const newOptions = [...criterion.options].sort((a, b) => {
                    const numA = Number(a.rate);
                    const numB = Number(b.rate);
                    if (a.rate === '') return 1;
                    if (b.rate === '') return -1;
                    if (!numA && !numB) return 0;
                    if (sort === 'asc') return numA - numB;
                    return numB - numA;
                });
                return {
                    ...criterion,
                    options: newOptions,
                };
            });
        });
    };

    // criterionId is the id of the criterion
    // optionId is the id of the option
    // name is one of ['title', 'description', 'rate']
    // value is the value of the onChange event
    const handleChange = (criterionId, optionId, name, value) => {
        if (name === 'rate' && isNaN(value)) {
            return message.error('Please enter number only.');
        }
        setData((current) => {
            return current.map((criterion) => {
                if (criterion.id !== criterionId) return criterion;
                return {
                    ...criterion,
                    options: criterion.options.map((option) => {
                        if (option.id !== optionId) return option;
                        return {
                            ...option,
                            [name]: value,
                        };
                    }),
                };
            });
        });
    };

    const handleClickAddCriterion = () => {
        if (!selectedCriterion.criterionName)
            return setSelectedCriterion((prev) => ({ ...prev, errors: { criterionName: true } }));
        const newCriterion = {
            id: uuid.v4(),
            title: selectedCriterion.criterionName,
            description: selectedCriterion.description,
            options: [
                {
                    id: uuid.v4(),
                    title: '',
                    description: '',
                    rate: '',
                },
                {
                    id: uuid.v4(),
                    title: '',
                    description: '',
                    rate: '',
                },
            ],
        };
        setData((current) => {
            return [...current, newCriterion];
        });
        handleCloseEditCriterionModal();
    };

    // criterionId is the id of the criterion
    const handleClickEditCriterion = (criterionId) => {
        const findCriterion = data?.find((criterion) => criterion.id === criterionId);
        setDialog({ ...dialog, createCriterion: { open: true } });
        setSelectedCriterion({
            ...selectedCriterion,
            criterionId,
            criterionName: findCriterion.title,
            description: findCriterion.description,
        });
    };

    const handleEditCriterion = () => {
        const changeData = data?.map((criterion) => {
            if (criterion.id === selectedCriterion.criterionId) {
                criterion = {
                    ...criterion,
                    title: selectedCriterion.criterionName,
                    description: selectedCriterion.description,
                };
            }
            return criterion;
        });
        setData(changeData);
        handleCloseEditCriterionModal();
    };

    // criterionId is the id of the criterion
    // index is the index at which the new option should be added
    const handleClickAddOption = (criterionId, index) => {
        setData((current) => {
            return current.map((criterion) => {
                if (criterion.id !== criterionId) return criterion;
                const newOption = {
                    id: uuid.v4(),
                    title: '',
                    description: '',
                    rate: '',
                };
                return {
                    ...criterion,
                    options: insertAtIndex(criterion.options, index, newOption),
                };
            });
        });
    };

    const handleDeleteOption = (criterionId, optionId) => {
        setData((current) => {
            return current.map((criterion) => {
                if (criterion.id !== criterionId) return criterion;
                const removeRate = criterion.options?.filter((option) => option.id !== optionId);

                return {
                    ...criterion,
                    options: removeRate,
                };
            });
        });
        setDialog({ ...dialog, deleteRate: { open: false } });
    };

    // criterionId is the id of the criterion
    // optionId is the id of the option
    const handleClickDeleteOption = (criterionId, optionId) => {
        setDialog({
            ...dialog,
            deleteRate: {
                open: true,
                message: 'Delete Rating?',
                description: 'This will permanently delete this rating, and cannot be undone.',
                variant: 'destructive',
                actionButtonText: 'Delete',
                onAction: () => handleDeleteOption(criterionId, optionId),
            },
        });
    };

    const handleDeleteCriterion = (criterionId) => {
        const removeCriterion = data.filter((criterion) => criterion.id !== criterionId);
        setData(removeCriterion);
        handleCloseEditCriterionModal();
    };

    const handleDragEnd = ({ source, destination, draggableId }) => {
        const srcIndex = source.index;
        const dstIndex = destination.index;
        if (srcIndex === dstIndex) return;
        setData((current) => {
            const newData = cloneDeep(current);
            newData.splice(srcIndex, 1);
            newData.splice(
                dstIndex,
                0,
                current.find(({ id }) => id === draggableId)
            );
            return newData;
        });
    };

    const handleCloseEditCriterionModal = () => {
        setDialog({ ...dialog, createCriterion: { open: false } });
        setSelectedCriterion({ criterionId: '', criterionName: '', description: '' });
    };

    return (
        <>
            <RubricTable
                sx={{ width: '100%' }}
                type={'create'}
                disabled={disabled}
                data={data}
                errors={errors}
                sort={sort}
                setSort={handleSort}
                onChange={handleChange}
                onClickAddCriterion={() => setDialog({ ...dialog, createCriterion: { open: true } })}
                onClickEditCriterion={handleClickEditCriterion}
                onClickAddOption={handleClickAddOption}
                onClickDeleteOption={handleClickDeleteOption}
                onDragEnd={handleDragEnd}
            />
            <Dialog open={dialog.createCriterion.open} onClose={() => handleCloseEditCriterionModal()}>
                <Stack spacing={7} sx={{ width: 600 }}>
                    <Text variant={'h6'}> Edit Criteria</Text>
                    <TextInput
                        error={selectedCriterion.errors?.criterionName}
                        label={'Criterion Name'}
                        name={'criterion name'}
                        size={'medium'}
                        value={selectedCriterion.criterionName}
                        placeholder={'Criterion Name'}
                        onChange={(e) => setSelectedCriterion({ ...selectedCriterion, criterionName: e.target.value })}
                    />
                    <TextArea
                        label={'Description'}
                        name={'description'}
                        rows={2}
                        size={'medium'}
                        placeholder={'Description'}
                        value={selectedCriterion.description}
                        onChange={(e) => setSelectedCriterion({ ...selectedCriterion, description: e.target.value })}
                    />
                    <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                        <Stack>
                            {selectedCriterion.criterionId && (
                                <Button
                                    startIcon={<Icon kind={'delete'} />}
                                    variant={'tertiary'}
                                    color={'destructive'}
                                    onClick={() => handleDeleteCriterion(selectedCriterion.criterionId)}
                                >
                                    Delete
                                </Button>
                            )}
                        </Stack>
                        <Stack direction={'row'} spacing={2}>
                            <Button variant={'tertiary'} onClick={() => handleCloseEditCriterionModal()}>
                                Cancel
                            </Button>
                            <Button
                                variant={'primary'}
                                onClick={() =>
                                    selectedCriterion.criterionId ? handleEditCriterion() : handleClickAddCriterion()
                                }
                            >
                                {selectedCriterion.criterionId ? 'Save Changes' : 'Create'}
                            </Button>
                        </Stack>
                    </Stack>
                </Stack>
            </Dialog>
            <AlertDialog
                message={dialog.deleteRate.message}
                description={dialog.deleteRate.description}
                note={dialog.deleteRate.note}
                open={dialog.deleteRate.open}
                variant={dialog.deleteRate.variant}
                actionButtonText={dialog.deleteRate.actionButtonText}
                cancelButtonText={dialog.deleteRate.cancelButtonText}
                onAction={dialog.deleteRate.onAction}
                onCancel={() => setDialog((current) => ({ ...current, deleteRate: { open: false } }))}
                onClose={() => setDialog((current) => ({ ...current, deleteRate: { open: false } }))}
            />
        </>
    );
};

export default RubricTableWrapper;
